"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.normalize = exports.ModelSettings = exports.createModel = exports.Model = void 0;
var events_1 = require("./events");
var helpers_1 = require("./helpers");
var type_1 = require("./type");
var format_1 = require("./format");
var entity_serializer_1 = require("./entity-serializer");
var resource_1 = require("./resource");
var globalization_1 = require("./globalization");
var event_scope_1 = require("./event-scope");
var valueTypes = { string: String, number: Number, date: Date, boolean: Boolean };
var Model = /** @class */ (function () {
    function Model(options, config) {
        this._readyProcessing = false;
        this.serializer = new entity_serializer_1.EntitySerializer();
        this.types = {};
        this.settings = new ModelSettings(config);
        this.entityRegistered = new events_1.Event();
        this.afterPropertySet = new events_1.Event();
        this.listChanged = new events_1.Event();
        this.eventScope = event_scope_1.EventScope.create(this.settings.eventScopeSettings);
        Object.defineProperty(this, "_formats", { enumerable: false, configurable: false, writable: true, value: {} });
        if (options) {
            this.extend(options);
        }
    }
    /**
     * Sets the default locale to use when a model's locale is not explicitly set
     * @param locale The default locale
     */
    Model.setDefaultLocale = function (locale) {
        resource_1.setDefaultLocale(locale);
    };
    /**
     * Defines global resource messages for the given locale
     * @param locale The locale to set messages for
     * @param resources The resources messages
     */
    Model.defineResources = function (locale, resources) {
        resource_1.defineResources(locale, resources);
    };
    Model.getResource = function (name, arg2, arg3) {
        var locale;
        var params;
        if (arguments.length === 2) {
            if (typeof arg2 === "string") {
                locale = arg2;
                params = null;
            }
            else if (typeof arg2 === "object") {
                locale = null;
                params = arg2;
            }
        }
        else if (arguments.length >= 3) {
            locale = arg2;
            params = arg3;
        }
        var resource = resource_1.getResource(name, locale);
        if (params)
            return helpers_1.replaceTokens(resource, params);
        return resource;
    };
    /**
     * Gets the resource with the specified name
     * @param name The resource name/key
     * @param params The parameters to use for string format substitution
     */
    Model.prototype.getResource = function (name, params) {
        if (params === void 0) { params = null; }
        var resource = resource_1.getResource(name, this.$resources, this.$locale);
        if (params)
            return helpers_1.replaceTokens(resource, params);
        return resource;
    };
    Model.prototype.resourceExists = function (name) {
        return resource_1.resourceExists(name, this.$resources, this.$locale);
    };
    /**
     * Formats a date as text using the given format string
     * @param date The date to format
     * @param format The format specifier
     */
    Model.prototype.formatDate = function (date, format) {
        return globalization_1.formatDate(date, format, this.$culture);
    };
    /**
     * Parses a date from text
     * @param text The text to parse
     */
    Model.prototype.parseDate = function (text, formats) {
        return globalization_1.parseDate(text, this.$culture, formats);
    };
    /**
     * Expands a date/time format string, which may be a predefined short format, into the equivalent full format strin
     * @param format The format string
     */
    Model.prototype.expandDateFormat = function (format) {
        return globalization_1.expandDateFormat(this.$culture.dateTimeFormat, format);
    };
    /**
     * Formats a number as text using the given format string
     * @param number The number to format
     * @param format The format specifier
     */
    Model.prototype.formatNumber = function (number, format) {
        return globalization_1.formatNumber(number, format, this.$culture);
    };
    /**
     * Parses a number from text
     * @param text The text to parse
     */
    Model.prototype.parseNumber = function (text, format) {
        return globalization_1.parseNumber(text, globalization_1.getNumberStyle(format), this.$culture);
    };
    /**
     * Extends the model with the specified type information.
     * @param options The set of model types to add and/or extend.
     */
    Model.prototype.extend = function (options) {
        var _this = this;
        // Use prepare() to defer property path resolution while the model is being extended
        this.prepare(function () {
            // Namespace
            if (options.$namespace) {
                // TODO: Guard against namespace being set after types have been created
                var $namespace = options.$namespace;
                if (!_this.$namespace) {
                    Object.defineProperty(_this, "$namespace", { configurable: false, enumerable: true, value: $namespace, writable: false });
                }
                else if ($namespace !== _this.$namespace) {
                    throw new Error("Cannot redefine namespace for model.");
                }
            }
            // Locale
            if (options.$locale && typeof options.$locale === "string") {
                // TODO: Guard against locale being set after types have been created
                var $locale = options.$locale;
                if (!_this.$locale) {
                    Object.defineProperty(_this, "$locale", { configurable: false, enumerable: true, value: $locale, writable: false });
                }
                else if ($locale !== _this.$locale) {
                    throw new Error("Cannot redefine locale for model.");
                }
            }
            // Resources
            if (options.$resources && typeof options.$resources === "object") {
                // TODO: Guard against resources being set after types have been created
                var $resources = options.$resources;
                if (!_this.$resources) {
                    Object.defineProperty(_this, "$resources", { configurable: false, enumerable: true, value: $resources, writable: false });
                }
                else if ($resources !== _this.$resources) {
                    throw new Error("Cannot redefine resources for model.");
                }
            }
            // Culture
            if (options.$culture) {
                var $culture = void 0;
                // TODO: Guard against culture being set after types have been created
                if (typeof options.$culture === "object") {
                    $culture = options.$culture;
                }
                else if (typeof options.$culture === "string") {
                    globalization_1.CultureInfo.setup();
                    if (globalization_1.CultureInfo.CurrentCulture.name === options.$culture) {
                        $culture = globalization_1.CultureInfo.CurrentCulture;
                    }
                    if (!$culture) {
                        throw new Error("Could not find culture '" + options.$culture + "'.");
                    }
                }
                if ($culture) {
                    if (!_this.$culture) {
                        Object.defineProperty(_this, "$culture", { configurable: false, enumerable: true, value: $culture, writable: false });
                    }
                    else if ($culture !== _this.$culture) {
                        throw new Error("Cannot redefine culture for model.");
                    }
                }
            }
            var typesToCreate = Object.keys(options).filter(function (typeName) { return !typeName.startsWith("$"); });
            var typesToInitialize = [];
            // Create New Types
            while (typesToCreate.length > 0) {
                var typeName = typesToCreate.splice(0, 1)[0];
                for (var typeNameIdx = -1, pos = typeName.length - 1, i = typeName.lastIndexOf(".", pos); i > 0; pos = i - 1, i = typeName.lastIndexOf(".", pos)) {
                    var typeNamespace = typeName.substring(0, i);
                    var typeNamespaceIdx = typesToCreate.indexOf(typeNamespace);
                    if (typeNamespaceIdx > typeNameIdx) {
                        if (process.env.NODE_ENV === "development") {
                            console.warn("Type '" + typeNamespace + "' should be created before type '" + typeName + "'.");
                        }
                        // Remove the current  type's "namespace" type and re-add the current type to the list
                        typesToCreate.splice(typeNamespaceIdx, 1);
                        typesToCreate.splice(0, 0, typeName);
                        typeNameIdx++;
                        // Resume the loop using the new namespace type (resetting index variables isn't necessary)
                        typeName = typeNamespace;
                    }
                }
                var typeOptions = options[typeName];
                var type = _this.types[typeName];
                typesToInitialize.push(typeName);
                if (!type) {
                    var baseType = null;
                    if (typeOptions.$extends) {
                        baseType = _this.types[typeOptions.$extends];
                        if (!baseType) {
                            throw new Error("Base type '" + typeOptions.$extends + "' for type '" + typeName + "' wasn't found.");
                        }
                    }
                    var format = typeOptions.$format;
                    type = new type_1.Type(_this, typeName, baseType, format);
                    _this.types[typeName] = type;
                }
            }
            // Extend Types
            for (var _i = 0, typesToInitialize_1 = typesToInitialize; _i < typesToInitialize_1.length; _i++) {
                var typeName = typesToInitialize_1[_i];
                var typeOptions = options[typeName];
                _this.types[typeName].extend(typeOptions);
            }
        });
    };
    /**
     * Prepares the model by invoking and extension function, which tracking the model
     * ready state to allow use of the @ready promise to defer property path resolution.
     * @param extend The function extending the model
     */
    Model.prototype.prepare = function (extend) {
        // Create a model initialization scope
        if (!this._readyCallbacks) {
            this._readyProcessing = false;
            // Create an array to track model initialization callbacks
            Object.defineProperty(this, "_readyCallbacks", { enumerable: false, configurable: true, writable: true, value: [] });
            // Extend the model
            extend();
            // Complete pending model initialization steps
            this._readyProcessing = true;
            for (var _i = 0, _a = this._readyCallbacks; _i < _a.length; _i++) {
                var init = _a[_i];
                init();
            }
            this._readyProcessing = false;
            delete this._readyCallbacks;
        }
        // Leverage the current model initialization scope
        else
            extend();
    };
    /**
     * Execute a function when the model is ready.
     * @param init The function to invoke when the model is ready.
     * @param enqueueWhileProcessing Determines whether the callback should be added to the queue while the queue is being processed.
     */
    Model.prototype.ready = function (callback, _a) {
        var _b = (_a === void 0 ? {} : _a).enqueueWhileProcessing, enqueueWhileProcessing = _b === void 0 ? true : _b;
        if (this._readyCallbacks && (!this._readyProcessing || enqueueWhileProcessing))
            this._readyCallbacks.push(callback);
        else
            callback();
    };
    /**
     * Gets the format for the specified property type and format string.
     * @param type The type the format is for
     * @param format The format template or specifier
     */
    Model.prototype.getFormat = function (type, format, formatEval) {
        // Return null if a format specifier was not provided
        if (!format) {
            return null;
        }
        // Get the format cache for the type
        var formats;
        if (type_1.isEntityType(type)) {
            formats = type.meta._formats;
        }
        else {
            formats = this._formats[type.name];
            if (!formats)
                formats = this._formats[type.name] = {};
        }
        // First see if the requested format is cached
        var f = formats[format];
        if (f) {
            return f;
        }
        // Otherwise, create and cache the format
        if (type_1.isEntityType(type)) {
            return (formats[format] = format_1.Format.fromTemplate(type.meta, format, formatEval));
        }
        else {
            // otherwise, call the format provider to create a new format
            return (formats[format] = format_1.createFormat(this, type, format));
        }
    };
    /**
     * Gets the javascript property type with the specified name.
     * @param type
     */
    Model.prototype.getJsType = function (type) {
        var jstype = type.toLowerCase() === "object" ? Object : valueTypes[type.toLowerCase()];
        if (!jstype) {
            var modelType = this.types[type];
            return modelType ? modelType.jstype : null;
        }
        return jstype;
    };
    return Model;
}());
exports.Model = Model;
// eslint-disable-next-line no-redeclare
function createModel(options, config) {
    return new Promise(function (resolve) {
        var model = new Model(options, config);
        model.ready(function () {
            resolve(model);
        });
    });
}
exports.createModel = createModel;
var ModelSettings = /** @class */ (function () {
    function ModelSettings(config) {
        // There is a slight speed cost to creating own properties,
        // which may be noticeable with very large object counts.
        this.createOwnProperties = false;
        this.autogeneratePropertyLabels = true;
        // Don't pollute the window object by default
        this.useGlobalObject = false;
        // Use sane defaults for event scope settings, i.e. "non-exiting" scope detection
        this.eventScopeSettings = event_scope_1.EVENT_SCOPE_DEFAULT_SETTINGS;
        this.createOwnProperties = config && !!config.createOwnProperties;
        if (config && config.autogeneratePropertyLabels === false)
            this.autogeneratePropertyLabels = false;
        this.useGlobalObject = config && !!config.useGlobalObject;
        this.eventScopeSettings = {
            maxExitingTransferCount: (config && typeof config.maxExitingEventScopeTransferCount === "number" ? config.maxExitingEventScopeTransferCount : null) || event_scope_1.EVENT_SCOPE_DEFAULT_SETTINGS.maxExitingTransferCount,
            maxDepth: (config && typeof config.maxEventScopeDepth === "number" ? config.maxEventScopeDepth : null) || event_scope_1.EVENT_SCOPE_DEFAULT_SETTINGS.maxDepth
        };
    }
    return ModelSettings;
}());
exports.ModelSettings = ModelSettings;
// eslint-disable-next-line no-redeclare
function normalize(val, format) {
    if (!val && val !== false)
        return val;
    if (val.constructor.name === "Date") {
        var dateFormat = typeof format === "string" ? format : format.specifier;
        if (dateFormat === "t") {
            // Set the date of the dateTime to January 1st, 1970
            var newDate = new Date(val.valueOf());
            newDate.setFullYear(1970);
            newDate.setMonth(0);
            newDate.setDate(1);
            return newDate;
        }
        else if (dateFormat === "d") {
            // Set the time of the dateTime to 12AM
            return new Date(val.getFullYear(), val.getMonth(), val.getDate());
        }
    }
    return val;
}
exports.normalize = normalize;
